import { normalizeApiError, normalizeError } from 'core/common/errors';
import { Logger } from 'core/common/services/logger/interfaces';
import { centsToDollars } from 'core/common/utils/money';
import {
  CardPaymentEntities,
  Currency,
  PaymentOrderDescription,
  PaymentType,
} from 'core/payments/entities';
import { UtmTags } from 'core/user/entities';
import { UtmTagsService } from 'core/user/services/utmTags';
import { ConversionsApiAttributionParameters, EventProperties, EventValue } from '../../entities';
import {
  AmplitudeService,
  AnalyticsService,
  BackendAnalyticsService,
  BingAdsService,
  GTMService,
  LtvPredictService,
  PinterestService,
  AppnebulaTikTokService,
  AppnebulaFacebookService,
} from '../../interfaces';
import { ConversionsAPIService } from '../conversionsAPI';

export class AnalyticsClientService implements AnalyticsService {
  private amplitudeService: AmplitudeService;

  private conversionsAPIService: ConversionsAPIService;

  private facebookService: AppnebulaFacebookService;

  private tikTokService: AppnebulaTikTokService;

  private pinterestService: PinterestService;

  private gtmService: GTMService;

  private utmTagsService: UtmTagsService;

  private ltvPredictService: LtvPredictService;

  private logger: Logger;

  private userId: string;

  private backendAnalyticsService: BackendAnalyticsService;

  private bingAdsService: BingAdsService;

  constructor(
    amplitudeService: AmplitudeService,
    conversionsAPIService: ConversionsAPIService,
    tikTokService: AppnebulaTikTokService,
    facebookService: AppnebulaFacebookService,
    pinterestService: PinterestService,
    gtmService: GTMService,
    utmTagsService: UtmTagsService,
    ltvPredictService: LtvPredictService,
    backendAnalyticsService: BackendAnalyticsService,
    logger: Logger,
    bingAdsService: BingAdsService,
  ) {
    this.logger = logger;

    this.userId = this.loadUserIdFromStorage();

    this.amplitudeService = amplitudeService;
    this.conversionsAPIService = conversionsAPIService;
    this.facebookService = facebookService;
    this.tikTokService = tikTokService;
    this.pinterestService = pinterestService;
    this.gtmService = gtmService;
    this.utmTagsService = utmTagsService;
    this.ltvPredictService = ltvPredictService;
    this.backendAnalyticsService = backendAnalyticsService;
    this.bingAdsService = bingAdsService;
    this.setUtmTags(this.utmTagsService.getUtmTags());
  }

  getAttributionParameters(): ConversionsApiAttributionParameters {
    return {
      ...this.conversionsAPIService.getAttributionParameters(),
    };
  }

  getDeviceId() {
    return this.amplitudeService.getDeviceId();
  }

  getSessionId() {
    return this.amplitudeService.getSessionId();
  }

  trackEvent(event: EventValue, properties: EventProperties = {}) {
    this.amplitudeService.trackEvent(event, properties);
  }

  funnelOpened({ funnelName, funnelLocale }: { funnelName: string; funnelLocale: string }) {
    this.tikTokService.onFunnelOpened();
    this.bingAdsService.onFunnelOpened({ funnelName, funnelLocale });
    this.pinterestService.onFunnelOpened({ funnelName, funnelLocale });
    this.gtmService.onFunnelOpened({ funnelName, funnelLocale });
    this.amplitudeService.funnelOpened(funnelName);
    this.ltvPredictService.setCurrentQuizName(funnelName);
  }

  flowOpened(flowName: string) {
    this.amplitudeService.flowOpened(flowName);
  }

  trackUserLanguage(language: string) {
    this.amplitudeService.trackUserLanguage(language);
  }

  addUserAssociatedId(associatedId: string) {
    this.amplitudeService.setUserProperties({ associated_id: associatedId });
  }

  optOtSellUserData() {
    this.gtmService.optOtSellUserData();
  }

  updateUserProperties(properties: Record<string, unknown>) {
    this.amplitudeService.setUserProperties(properties);
  }

  setUserId(userId: string) {
    this.amplitudeService.setUserId(userId);
    this.userId = userId;
  }

  setIsUserTester(isTester: boolean) {
    this.amplitudeService.setUserProperties({
      tester: isTester,
    });
  }

  paymentPlanChosen(orderId: string) {
    try {
      this.facebookService.addToCart(orderId);
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(error);
    }
  }

  paymentFormOpened(orderId: string) {
    try {
      this.facebookService.initiateCheckout(orderId);
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(error);
    }
  }

  policyPageOpened(pageName: string, contextName: string) {
    this.amplitudeService.policyPageOpened(pageName, contextName, this.userId);
  }

  faqPageOpened(pageName: string, contextName: string) {
    this.amplitudeService.faqPageOpened(pageName, contextName, this.userId);
  }

  contactUsPageOpened(pageName: string, contextName: string) {
    this.amplitudeService.contactUsPageOpened(pageName, contextName, this.userId);
  }

  setUserExperimentGroupName(experimentName: string, groupName: string) {
    try {
      this.amplitudeService.setUserExperimentGroupName(experimentName, groupName);
      this.backendAnalyticsService.setSplitExperimentProperties({ experimentName, groupName });
    } catch (e) {
      const error = normalizeError(e);

      this.logger.error(error);
    }
  }

  launch() {
    this.amplitudeService.launch();
  }

  paymentClick(paymentType: PaymentType) {
    switch (paymentType) {
      case PaymentType.APPLE_PAY:
        return this.amplitudeService.applePayClick();
      case PaymentType.PAYPAL:
        return this.amplitudeService.paypalPayClick();
      case PaymentType.GOOGLE_PAY:
        return this.amplitudeService.googlePayClick();
      default:
        return this.amplitudeService.paymentCardClick();
    }
  }

  paymentCardChosen(paymentType: CardPaymentEntities) {
    if (paymentType === CardPaymentEntities.APPLE_BTN) {
      this.amplitudeService.appleBtnChosen(this.userId);
      return;
    }

    if (paymentType === CardPaymentEntities.GOOGLE_BTN) {
      this.amplitudeService.googleBtnChosen(this.userId);
      return;
    }

    this.amplitudeService.paymentCardChosen(this.userId);
  }

  paymentPaypalChosen() {
    this.amplitudeService.paymentPaypalChosen(this.userId);
  }

  paymentRetried() {
    this.amplitudeService.paymentRetried(this.userId);
  }

  paymentError(reason: string) {
    this.amplitudeService.paymentError(this.userId, reason);
  }

  async paymentSuccess(paymentData: {
    productId: string;
    paymentProviderOrder: PaymentOrderDescription;
    orderId: string;
    paymentMethod: string;
    currency: Currency;
    email: string;
  }) {
    const { productId, paymentProviderOrder, orderId, currency, email } = paymentData;

    this.amplitudeService.paymentSuccess(paymentData);

    const price = centsToDollars(paymentProviderOrder.amount);
    const ltvPredict = (await this.ltvPredictService.getLTVPredict(productId)) || price;

    try {
      this.gtmService.purchase({
        price,
        orderId: paymentProviderOrder.order_id,
        productId,
        ltvPredict,
        currency,
        email,
      });
      this.tikTokService.paymentSuccess({ price, orderId, productId, ltvPredict, currency, email });
      this.facebookService.purchase({ ltvPredict, orderId, currency });
      this.pinterestService.checkout({ price, orderId, currency });
      this.bingAdsService.purchase({
        price,
        orderId,
        productId,
        ltvPredict,
        currency,
        email,
      });
    } catch (e) {
      const error = normalizeError(e);
      this.logger.error(error);
    }
  }

  lead(orderId: string) {
    this.pinterestService.lead(orderId);
  }

  emailUpsellScreenOpen(context: string) {
    this.amplitudeService.emailUpsellScreenOpen(context);
  }

  addToCart({
    price,
    trialPrice,
    productId,
    email,
    orderId,
    currency,
  }: {
    price: number;
    trialPrice?: number;
    productId: string;
    email: string;
    orderId: string;
    currency: Currency;
  }) {
    this.gtmService.addToCart({ price, productId, email, orderId, currency });
    this.tikTokService.addToCard({ price, productId, email, orderId, currency });

    if (trialPrice) {
      this.bingAdsService.addToCart({ productId, email, orderId, currency, trialPrice });
    }
  }

  initiateCheckout({
    price,
    productId,
    email,
    orderId,
    currency,
    trialPrice,
  }: {
    price: number;
    productId: string;
    email: string;
    orderId: string;
    currency: Currency;
    trialPrice?: number;
  }) {
    this.gtmService.initiateCheckout({ price, productId, email, orderId, currency });
    this.tikTokService.initiateCheckout({ price, productId, email, orderId, currency });

    if (trialPrice) {
      this.bingAdsService.beginCheckout({ trialPrice, productId, email, orderId, currency });
    }
  }

  addEmail(email: string, userId: string) {
    this.gtmService.addEmail(email, userId);
    this.tikTokService.addEmail(email, userId);
    this.amplitudeService.setUserEmail(email);
  }

  addUserId(id: string) {
    this.amplitudeService.setUserIdProperty(id);
  }

  purchaseSucceed(productName: string) {
    this.amplitudeService.purchaseSucceed(productName);
  }

  purchaseError(productName: string) {
    this.amplitudeService.purchaseError(productName);
  }

  setUserAge(age: number) {
    this.amplitudeService.setUserAge(age);
    this.tikTokService.addBirthdate();
  }

  setFeatureFlagsAttributes(attributes: Record<string, unknown>) {
    this.amplitudeService.setFeatureFlagsAttributes(attributes);
  }

  private loadUserIdFromStorage(): string {
    try {
      const userStorage = localStorage.getItem('nebula') || '';
      const userId = userStorage ? JSON.parse(userStorage).userProfile.id : '';
      return userId;
    } catch (err) {
      const error = normalizeApiError(err);
      this.logger.error(error);
      return '';
    }
  }

  private setUtmTags(tags: UtmTags) {
    this.amplitudeService.setUserProperties({
      media_source: tags.utm_source,
      ...tags,
    });
  }
}
